import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';

import {
  fetchOrgIntakeQueue,
  revealLeadContact,
  setOrgIntakeClinic,
} from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { formatFollowupAge } from '@/dashboard/clinical-workbench/CocFollowupCockpit';
import LeadContactRevealControl from '@/dashboard/owner/LeadContactReveal';
import {
  NO_NAME_LABEL,
  ORG_INTAKE_EMPTY_HINT,
  ORG_INTAKE_EMPTY_TITLE,
  ORG_INTAKE_HINT,
  ORG_INTAKE_RESTRICTED_COPY,
  ORG_INTAKE_SET_ONCE_NOTE,
  ORG_INTAKE_TITLE,
  ORG_INTAKE_TRUNCATED_NOTE,
  ORG_INTAKE_UNAVAILABLE_COPY,
  assignedLeadDetailHref,
  clinicOptionLabel,
  isAccessRestricted,
  pendingCountLabel,
  removeQueueRow,
  requirementBadge,
  reviewReasonLabel,
  setClinicErrorMessage,
  setClinicSuccessText,
  type OrgIntakeClinicOption,
  type OrgIntakeQueueResult,
  type OrgIntakeQueueRow,
} from '@/dashboard/org-intake/org-intake-view';

/**
 * AdoraCare -- org-intake pending-clinic queue page (ORG-INTAKE-QUEUE-V1,
 * B2 / Story 5.4; initially configured for COC in the Partha workflow).
 *
 * LIST-FIRST, mobile-friendly (the #183 CocFollowupCockpit row idiom:
 * flex-wrap rows, no table). Each row is a COMPACT SUMMARY of REAL fields
 * only -- carved name / source chip / src token / ad-click / requirement
 * bucket (only when captured) / neutral age / the B1 reconciliation-review
 * marker -- plus the EXISTING audited phone-reveal control (pure reuse of
 * LeadContactRevealControl; flags/data missing renders its own honest
 * no-value / disabled states, never a fake affordance) and the SET-ONCE
 * clinic assignment: an inline expand with the org's clinic picker + an
 * explicit Confirm (the AppointmentWorkbench inline-confirm convention --
 * no modal exists in this codebase).
 *
 * After a successful assignment the row leaves the queue (it is no longer
 * pending) and the success feedback offers the EXISTING lead-detail route
 * (/dashboard/leads/:id) ONLY when me.leadsWorkspaceEnabled -- the detail
 * surface cannot render otherwise, and a dead link is a fake affordance.
 * Pre-assignment rows carry no detail link: the workspace read model is
 * clinic-fenced and cannot render a clinic-less lead for an operator
 * (ratified at the B2 design gate).
 *
 * Conflict UX follows the Workboard convention: inline aria-live feedback
 * banner; the Refresh affordance appears ONLY for concurrency conflicts
 * (setClinicErrorMessage's code-first mapping); per-row buttons disable
 * while that row's request is in flight.
 */

// ---- Presentational state ----

export type OrgIntakeState =
  | { kind: 'loading' }
  | { kind: 'error'; restricted: boolean }
  | { kind: 'ready'; data: OrgIntakeQueueResult };

/** Honest read-failure states -- NEVER a fabricated empty queue. */
export const ORG_INTAKE_ERROR_STATE: OrgIntakeState = {
  kind: 'error',
  restricted: false,
};
export const ORG_INTAKE_RESTRICTED_STATE: OrgIntakeState = {
  kind: 'error',
  restricted: true,
};

export interface OrgIntakeFeedback {
  readonly kind: 'success' | 'error';
  readonly text: string;
  readonly showRefresh: boolean;
  readonly link: { href: string; label: string } | null;
}

/** Inline aria-live feedback banner (the shared .workboard__feedback classes). */
export function OrgIntakeFeedbackBanner({
  feedback,
  onRefresh,
}: {
  feedback: OrgIntakeFeedback | null;
  onRefresh: () => void;
}): JSX.Element | null {
  if (feedback === null) return null;
  return (
    <div
      className={
        feedback.kind === 'success'
          ? 'workboard__feedback workboard__feedback--success'
          : 'workboard__feedback workboard__feedback--error'
      }
      role="status"
      aria-live="polite"
      data-testid="org-intake-feedback"
    >
      <span>{feedback.text}</span>
      {feedback.link !== null ? (
        <Link to={feedback.link.href} data-testid="org-intake-feedback-link">
          {feedback.link.label}
        </Link>
      ) : null}
      {feedback.showRefresh ? (
        <button
          type="button"
          className="ac-btn ac-btn--secondary ac-btn--sm"
          onClick={onRefresh}
        >
          Refresh
        </button>
      ) : null}
    </div>
  );
}

// ---- One queue row (compact summary + reveal + inline assign) ----

export interface OrgIntakeRowViewProps {
  readonly row: OrgIntakeQueueRow;
  readonly nowMs: number;
  readonly busy: boolean;
  readonly expanded: boolean;
  readonly clinics: readonly OrgIntakeClinicOption[];
  readonly selectedClinicId: string;
  readonly onExpand: (leadId: number) => void;
  readonly onCancel: () => void;
  readonly onClinicChange: (value: string) => void;
  readonly onConfirm: (leadId: number) => void;
  /** The audited reveal control (injected so SSR tests render without state). */
  readonly revealControl?: React.ReactNode;
}

export function OrgIntakeRowView({
  row,
  nowMs,
  busy,
  expanded,
  clinics,
  selectedClinicId,
  onExpand,
  onCancel,
  onClinicChange,
  onConfirm,
  revealControl,
}: OrgIntakeRowViewProps): JSX.Element {
  const bucket = requirementBadge(row.requirementBucket);
  return (
    <li className="coc-cockpit__row org-intake__row" data-testid={`org-intake-row-${row.leadId}`}>
      <div className="coc-cockpit__row-main">
        <span className="coc-cockpit__name" data-testid={`org-intake-name-${row.leadId}`}>
          {row.name ?? NO_NAME_LABEL}
        </span>
        <span className="cockpit-pill cockpit-pill--muted">
          Lead #{row.leadId}
        </span>
        <span className="coc-cockpit__age" data-testid={`org-intake-age-${row.leadId}`}>
          {formatFollowupAge(row.createdAt, nowMs)}
        </span>
      </div>
      <div className="org-intake__chips" data-testid={`org-intake-chips-${row.leadId}`}>
        <span className="badge badge--info">{row.sourceNormalized}</span>
        <span className="badge badge--warning">Clinic pending</span>
        {row.srcToken !== null ? (
          <span className="badge" data-testid={`org-intake-src-${row.leadId}`}>
            src: {row.srcToken}
          </span>
        ) : null}
        {row.adClick ? (
          <span className="badge badge--info" data-testid={`org-intake-adclick-${row.leadId}`}>
            Ad click
          </span>
        ) : null}
        {bucket !== null ? <span className="badge">{bucket}</span> : null}
        {row.review !== null ? (
          <span
            className="badge badge--danger"
            data-testid={`org-intake-review-${row.leadId}`}
          >
            {reviewReasonLabel(row.review.reason)}
          </span>
        ) : null}
      </div>
      {revealControl ?? null}
      {expanded ? (
        <div className="org-intake__assign" data-testid={`org-intake-assign-${row.leadId}`}>
          <label>
            Clinic:{' '}
            <select
              data-testid={`org-intake-clinic-select-${row.leadId}`}
              value={selectedClinicId}
              disabled={busy}
              onChange={(e) => onClinicChange(e.target.value)}
            >
              <option value="">Select a clinic...</option>
              {clinics.map((c) => (
                <option key={c.clinicId} value={String(c.clinicId)}>
                  {clinicOptionLabel(c)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ac-btn ac-btn--primary ac-btn--sm"
            data-testid={`org-intake-confirm-${row.leadId}`}
            disabled={busy || selectedClinicId === ''}
            onClick={() => onConfirm(row.leadId)}
          >
            Confirm clinic
          </button>
          <button
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <span className="lead360-muted org-intake__set-once">
            {ORG_INTAKE_SET_ONCE_NOTE}
          </span>
        </div>
      ) : (
        <div className="coc-cockpit__row-actions">
          <button
            type="button"
            className="ac-btn ac-btn--primary ac-btn--sm"
            data-testid={`org-intake-expand-${row.leadId}`}
            disabled={busy}
            onClick={() => onExpand(row.leadId)}
          >
            Assign clinic
          </button>
        </div>
      )}
    </li>
  );
}

// ---- The queue view (pure -- renderToString-testable) ----

export interface OrgIntakeQueueViewProps {
  readonly state: OrgIntakeState;
  readonly nowMs: number;
  readonly busyLeadId: number | null;
  readonly expandedLeadId: number | null;
  readonly selectedClinicId: string;
  readonly feedback: OrgIntakeFeedback | null;
  readonly onRefresh: () => void;
  readonly onExpand: (leadId: number) => void;
  readonly onCancel: () => void;
  readonly onClinicChange: (value: string) => void;
  readonly onConfirm: (leadId: number) => void;
  readonly renderReveal?: (row: OrgIntakeQueueRow) => React.ReactNode;
}

export function OrgIntakeQueueView({
  state,
  nowMs,
  busyLeadId,
  expandedLeadId,
  selectedClinicId,
  feedback,
  onRefresh,
  onExpand,
  onCancel,
  onClinicChange,
  onConfirm,
  renderReveal,
}: OrgIntakeQueueViewProps): JSX.Element {
  return (
    <section
      className="clinic-table coc-cockpit org-intake"
      data-testid="org-intake"
      aria-label={ORG_INTAKE_TITLE}
    >
      <div className="coc-cockpit__head">
        <h2 className="clinic-table__title">{ORG_INTAKE_TITLE}</h2>
        {state.kind === 'ready' ? (
          <span className="coc-cockpit__count" data-testid="org-intake-count">
            {pendingCountLabel(state.data.rows.length)}
          </span>
        ) : null}
      </div>
      <p className="clinic-workbench__hint" data-testid="org-intake-hint">
        {ORG_INTAKE_HINT}
      </p>
      {state.kind === 'ready' &&
      state.data.role === 'owner' &&
      state.data.ownerRole !== 'owner' ? (
        <p className="clinic-workbench__hint" data-testid="org-intake-owner-note">
          Worked by: {state.data.ownerRole} (configured in Routing settings).
        </p>
      ) : null}

      <OrgIntakeFeedbackBanner feedback={feedback} onRefresh={onRefresh} />

      {state.kind === 'loading' ? (
        <p className="followups-empty__sub" role="status">
          Loading the intake queue...
        </p>
      ) : state.kind === 'error' ? (
        <p
          className="followups-empty__sub"
          role="status"
          data-testid={
            state.restricted ? 'org-intake-restricted' : 'org-intake-error'
          }
        >
          {state.restricted
            ? ORG_INTAKE_RESTRICTED_COPY
            : ORG_INTAKE_UNAVAILABLE_COPY}
        </p>
      ) : state.data.rows.length === 0 ? (
        <div className="followups-empty" role="status" data-testid="org-intake-empty">
          <Inbox className="followups-empty__icon" size={20} aria-hidden="true" />
          <p className="followups-empty__title">{ORG_INTAKE_EMPTY_TITLE}</p>
          <p className="followups-empty__sub">{ORG_INTAKE_EMPTY_HINT}</p>
        </div>
      ) : (
        <>
          <ul className="coc-cockpit__list" data-testid="org-intake-list">
            {state.data.rows.map((row) => (
              <OrgIntakeRowView
                key={String(row.leadId)}
                row={row}
                nowMs={nowMs}
                busy={busyLeadId === row.leadId}
                expanded={expandedLeadId === row.leadId}
                clinics={state.data.clinics}
                selectedClinicId={
                  expandedLeadId === row.leadId ? selectedClinicId : ''
                }
                onExpand={onExpand}
                onCancel={onCancel}
                onClinicChange={onClinicChange}
                onConfirm={onConfirm}
                revealControl={renderReveal?.(row)}
              />
            ))}
          </ul>
          {!state.data.isComplete ? (
            <p className="clinic-workbench__note" data-testid="org-intake-truncated">
              {ORG_INTAKE_TRUNCATED_NOTE}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

// ---- Stateful wirer (default export) ----

export default function OrgIntakePage(): JSX.Element {
  const meCtx = useDashboardMeContext();
  const [state, setState] = useState<OrgIntakeState>({ kind: 'loading' });
  const [busyLeadId, setBusyLeadId] = useState<number | null>(null);
  const [expandedLeadId, setExpandedLeadId] = useState<number | null>(null);
  const [selectedClinicId, setSelectedClinicId] = useState<string>('');
  const [feedback, setFeedback] = useState<OrgIntakeFeedback | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  const load = useCallback(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    setFeedback(null);
    setExpandedLeadId(null);
    setSelectedClinicId('');
    setNowMs(Date.now());
    fetchOrgIntakeQueue()
      .then((data) => {
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch((err: unknown) => {
        // Honest unavailable/restricted states -- never a fabricated empty.
        if (!cancelled) {
          setState(
            isAccessRestricted(err)
              ? ORG_INTAKE_RESTRICTED_STATE
              : ORG_INTAKE_ERROR_STATE,
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const onExpand = useCallback((leadId: number) => {
    setExpandedLeadId(leadId);
    setSelectedClinicId('');
    setFeedback(null);
  }, []);

  const onCancel = useCallback(() => {
    setExpandedLeadId(null);
    setSelectedClinicId('');
  }, []);

  const onConfirm = useCallback(
    (leadId: number) => {
      const clinicId = Number(selectedClinicId);
      if (!Number.isInteger(clinicId) || clinicId < 1) return;
      setBusyLeadId(leadId);
      setOrgIntakeClinic(leadId, clinicId)
        .then((result) => {
          // The lead left the pending queue -- drop its row in place.
          setState((prev) =>
            prev.kind === 'ready'
              ? {
                  kind: 'ready',
                  data: {
                    ...prev.data,
                    rows: removeQueueRow(prev.data.rows, leadId),
                  },
                }
              : prev,
          );
          setExpandedLeadId(null);
          setSelectedClinicId('');
          setFeedback({
            kind: 'success',
            text: setClinicSuccessText(result),
            showRefresh: false,
            // The detail link ONLY when the workspace surface can render
            // it for this user AND the assigned clinic is inside the
            // caller's own verified scope (server-derived) -- the
            // clinic-fenced detail surface would 401 otherwise. No dead
            // links.
            link:
              meCtx?.me?.leadsWorkspaceEnabled === true &&
              result.clinicInCallerScope === true
                ? {
                    href: assignedLeadDetailHref(result.leadId, result.clinicId),
                    label: 'Open lead',
                  }
                : null,
          });
        })
        .catch((err: unknown) => {
          const mapped = setClinicErrorMessage(err);
          setFeedback({
            kind: 'error',
            text: mapped.text,
            showRefresh: mapped.showRefresh,
            link: null,
          });
        })
        .finally(() => {
          setBusyLeadId((b) => (b === leadId ? null : b));
        });
    },
    [selectedClinicId, meCtx],
  );

  return (
    <OrgIntakeQueueView
      state={state}
      nowMs={nowMs}
      busyLeadId={busyLeadId}
      expandedLeadId={expandedLeadId}
      selectedClinicId={selectedClinicId}
      feedback={feedback}
      onRefresh={load}
      onExpand={onExpand}
      onCancel={onCancel}
      onClinicChange={setSelectedClinicId}
      onConfirm={onConfirm}
      renderReveal={(row) => (
        <LeadContactRevealControl
          availability={row.phoneAvailability}
          reveal={(purpose) => revealLeadContact(String(row.leadId), purpose)}
        />
      )}
    />
  );
}
